import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as BABYLON from '@babylonjs/core'
import '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline'
import { StudioCameraState, StudioView } from '../types'
import { HOME_CAMERA, ORBIT_SWEEP_ALPHA } from '../constants'

/** How long the camera must sit still before its pose is snapshotted for autosave — mirrors data.persist()'s own debounce so a mid-drag orbit never spams the save pipeline. */
const CAMERA_SETTLE_MS = 600

export interface FlyToTarget {
  side: -1 | 0 | 1
  center: BABYLON.Vector3
  width: number
}

interface CameraGoal {
  alpha?: number
  beta?: number
  radius?: number
  target?: BABYLON.Vector3
}

export interface ClassicFraming {
  target: BABYLON.Vector3
  radius: number
  radiusMax: number
  /** See `useStudioLegacyRoom`'s `ClassicFraming` — caps beta so a wide/shallow room's orbit can't tilt far enough to fly the camera through its own near wall. */
  betaCap: number
  /** See `useStudioLegacyRoom`'s `ClassicFraming` — world-space X bounds the orbit slider pans `target` across, since `alpha` itself is locked here. */
  panMinX: number
  panMaxX: number
}

/** Virtual WASD — used by guest mobile walk pad (keyboard still works via UniversalCamera keys). */
export interface WalkPadInput {
  forward: number
  strafe: number
}

export interface StudioCameraRigApi {
  view: StudioView
  isPresent: boolean
  zoomPercent: number
  setView: (view: StudioView) => void
  flyTo: (target: FlyToTarget) => void
  enterPresent: () => void
  exitPresent: () => void
  /** For useStudioRoom's dollhouse cutaway — which camera is active + whether it's the first-person one. */
  /** Snapshot for React deps — prefer `getActiveCameraInfo` for live cutaway. */
  activeCameraInfo: { camera: BABYLON.Camera | null; isInside: boolean }
  /** Read orbit/walk refs live each frame (cameras exist only after scene mount). */
  getActiveCameraInfo: () => { camera: BABYLON.Camera | null; isInside: boolean }
  /** False for a Classic room — no collision over its authored geometry, so first-person Walk is hidden (see StudioViewSwitcher) rather than left silently broken. Present still works there — `enterPresent` falls back to (the already Classic-bounded) Orbit view instead of Walk. */
  canWalk: boolean
  /** Hold/release on-screen walk controls. Values typically -1 | 0 | 1. */
  setWalkPad: (input: Partial<WalkPadInput>) => void
  /**
   * Latest settled camera pose, `CAMERA_SETTLE_MS` after the user stops
   * moving it — `null` until the first settle. Feed straight into
   * `data.persist()`'s `camera` field alongside room/arts/pins.
   */
  cameraState: StudioCameraState | null
  /**
   * "Look left/right" sweep, 0-100 — drives `StudioOrbitSlider`. For a
   * procedural room this is orbit `alpha` mapped onto `ORBIT_SWEEP_ALPHA`;
   * for a Classic room `alpha` is locked (see `CLASSIC_BETA` above) so it's
   * `target.x` mapped onto `classicFraming.panMinX..panMaxX` instead — the
   * same axis the legacy pattern editor's own `SetCameraHorizontalPosition`
   * panned (see `useStudioLegacyRoom`'s `ClassicFraming`). Resyncs every
   * frame the orbit camera is active — goal-driven moves (slider drag,
   * `flyTo`, view switches) and raw mouse drag alike — same as
   * `zoomPercent` below.
   */
  orbitAlphaPercent: number
  /** Slider → camera: damped fly to the given sweep/pan percent (alpha sweep, or Classic X-pan — see `orbitAlphaPercent`). */
  setOrbitAlphaPercent: (percent: number) => void
  /** Nudge `orbit.radius` one step closer/farther — see `ZOOM_STEP_FACTOR`. No-op before the orbit camera mounts. */
  zoomIn: () => void
  zoomOut: () => void
  /** Snaps `orbit.radius` back to this mode's home distance — zoom only, doesn't touch pan/alpha/beta. Pan gets its own separate "Recenter pan" in `StudioOrbitSlider`; these are two different axes with two different, clearly-labeled resets. */
  resetZoom: () => void
  /** Full pan+tilt+zoom(+Walk position) reset to this mode's starting pose — see its doc comment for why Present/Guest need this instead of `resetZoom` + a pan reset. */
  resetCamera: () => void
}

/** Clamps into `ORBIT_SWEEP_ALPHA` before mapping — an out-of-range alpha (e.g. a saved pose from before the range existed) should read as its nearest end, not spill outside 0-100. */
function alphaToSweepPercent(alpha: number): number {
  const { min, max } = ORBIT_SWEEP_ALPHA
  const clamped = Math.max(min, Math.min(max, alpha))
  return ((clamped - min) / (max - min)) * 100
}

function sweepPercentToAlpha(percent: number): number {
  const { min, max } = ORBIT_SWEEP_ALPHA
  const clamped = Math.max(0, Math.min(100, percent))
  return min + (max - min) * (clamped / 100)
}

/** Classic mirror of `alphaToSweepPercent` — pans `target.x` across the room's own loaded width instead of sweeping `alpha`. A degenerate/zero-width room (`panMinX === panMaxX`) has nothing to pan across; reads as centered rather than dividing by zero. */
function panXToSweepPercent(x: number, framing: ClassicFraming): number {
  const { panMinX, panMaxX } = framing
  if (panMaxX <= panMinX) return 50
  const clamped = Math.max(panMinX, Math.min(panMaxX, x))
  return ((clamped - panMinX) / (panMaxX - panMinX)) * 100
}

function sweepPercentToPanX(percent: number, framing: ClassicFraming): number {
  const { panMinX, panMaxX } = framing
  const clamped = Math.max(0, Math.min(100, percent))
  return panMinX + (panMaxX - panMinX) * (clamped / 100)
}

// Neutral holding pose for Classic mode before its `.babylon` room has
// finished loading (or if it never does — see `isClassicMode` below). This
// intentionally mirrors the locked top-down angle `classicFraming` will set
// once real bounds arrive (alpha = π/2, low beta) with a generic radius/
// target — NOT `HOME_CAMERA`'s walkable eye-level pose, which assumes a
// procedural floor that Classic mode never builds (see `useStudioRoom`'s
// early return for `mode === 'classic'`). Landing eye-level over a room with
// no floor mesh at all is exactly what read as "camera under the floor."
const CLASSIC_HOLD = { alpha: Math.PI / 2, beta: 0.2, radius: 11, target: new BABYLON.Vector3(0, 0, 0) }

// Classic rooms are legacy `.babylon` dioramas authored for the legacy
// pattern editor's fixed bird's-eye camera (see `createCamera` in
// src/pages/pattern/renderer/main.js) — alpha stays locked at π/2 (no
// horizontal orbit; these rooms weren't built to be seen from the side),
// but beta keeps a real — if narrow — range so "camera orbits" (the
// Studio's own Classic-tab copy) actually means something here instead of
// freezing on one frame. Kept close to top-down since these rooms' "floor"
// meshes are often vertical planes that only read correctly near that
// angle; CLASSIC_BETA is the reset/default within the range, not a hard lock.
const CLASSIC_BETA = CLASSIC_HOLD.beta
const CLASSIC_BETA_MIN = 0.05
const CLASSIC_BETA_MAX = 0.45
const CLASSIC_BETA_PLAN = 0.06

/**
 * `classicFraming.betaCap` (see its own doc comment in `useStudioLegacyRoom`)
 * is sized for the *worst case* — max zoom-out, `radiusMax` — so it comes
 * out tiny for any room whose fit-to-room `radiusMax` dwarfs its own depth
 * (a wide/shallow room like a 3-print Classic 11 can land well under
 * `CLASSIC_BETA_MIN`). Using that single worst-case number as a *static*
 * beta ceiling at every zoom level made tilt unusable — even fully locked,
 * once it undercuts `CLASSIC_BETA_MIN` and collapses lower/upper to the same
 * value — at the room's actual starting/zoomed-in radius, where the "camera
 * through the near wall" risk that number is guarding against barely
 * applies. `betaCap` already encodes `asin(safeZOffset / radiusMax)`; since
 * `radiusMax * sin(betaCap) === safeZOffset`, this recovers that same margin
 * and re-solves the identical check against whatever radius the camera's
 * actually at right now — tight only when actually zoomed out near
 * `radiusMax`, opening back up on zoom-in — instead of one fixed worst-case
 * number applied regardless of zoom.
 */
function classicBetaCapAt(framing: ClassicFraming, radius: number): number {
  const safeZOffset = framing.radiusMax * Math.sin(framing.betaCap)
  return Math.max(0.02, Math.asin(Math.min(1, safeZOffset / Math.max(0.01, radius))))
}

// Mirrors the legacy pattern editor's own `camera.lowerRadiusLimit = 1.2`
// (see `createCamera` in src/pages/pattern/renderer/main.js) — a single
// fixed near-zoom limit used for every one of these 14 rooms regardless of
// size, never derived from that room's own radius/footprint. The reframe
// effect below used to compute this as a fraction of the room's *starting*
// radius instead, which meant a wide room (bigger starting radius) got a
// proportionally bigger zoom-in floor too — you could get close to a small
// room's prints but not a big room's, which is backwards from what "zoom
// in" should ever feel like.
const CLASSIC_LOWER_RADIUS = 1.2

/** Radius multiplier per zoomIn()/zoomOut() click — ~18%/22% step, snappy but not jarring. Divides to zoom in (smaller radius = closer), multiplies to zoom out. */
const ZOOM_STEP_FACTOR = 1.22

/**
 * Owns the orbit/plan/walk camera trio, the damped fly-to used for both
 * "click a print" and view switching, present mode, and the bloom/ACES
 * post pipeline. Ported from the prototype's camera section.
 *
 * `classicFraming` — when a "Classic" legacy room is loaded (see
 * `useStudioLegacyRoom`), pass its authored bounds/radius here instead of
 * the procedural shell's fixed `HOME_CAMERA`. Loaded geometry has no
 * collision, so Walk is locked out for the duration (falls back to Orbit).
 *
 * `isClassicMode` — true whenever the room *wants* to be Classic, even
 * before (or if never) `classicFraming` resolves. Known synchronously from
 * `room.mode` (unlike `classicFraming`, which only arrives after the async
 * `.babylon` load completes) — used so the brief load window, or a room
 * stuck in Classic mode with no `classicTemplateId`, holds `CLASSIC_HOLD`
 * instead of snapping to the walkable `HOME_CAMERA`.
 */
export function useStudioCameraRig(
  scene: BABYLON.Scene | null,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  classicFraming?: ClassicFraming | null,
  isClassicMode = false,
  /**
   * Extra zoom-in multiplier applied on top of a Classic room's own
   * computed `radius`/`radiusMax` (default 1 — untouched, what the
   * authenticated Studio editor uses). That computation sizes the room's
   * *full* loaded footprint (including its side walls, often far wider
   * than the actual print-bearing wall face) into frame, deliberately
   * conservative since the editor's canvas can be squeezed by its
   * inspector panel. The read-only guest route is always full-bleed with
   * no panel to leave headroom for, so it passes a sub-1 value here to
   * fill the frame with the prints themselves instead of the room's
   * full (and mostly blank) width.
   */
  classicZoom = 1,
  /**
   * Last-saved pose to open on instead of `HOME_CAMERA`/`CLASSIC_HOLD` —
   * read once at mount only (see the mount effect below), same "seed once"
   * convention as `useStudioRoom`/`useStudioArts`'s own initial props.
   */
  initialCamera?: StudioCameraState | null,
): StudioCameraRigApi {
  const [view, setViewState] = useState<StudioView>('orbit')
  const [isPresent, setIsPresent] = useState(false)
  const [zoomPercent, setZoomPercent] = useState(100)
  const [cameraState, setCameraState] = useState<StudioCameraState | null>(null)
  // Classic's real pan bounds (`classicFraming.panMinX/panMaxX`) aren't known
  // until its `.babylon` file finishes loading (async) — read as centered
  // until then; the reframing effect below corrects it the instant real
  // bounds arrive, same "holding pose" pattern `CLASSIC_HOLD` uses for alpha/beta.
  const [orbitAlphaPercent, setOrbitAlphaPercentState] = useState(() =>
    isClassicMode ? 50 : alphaToSweepPercent(initialCamera?.alpha ?? HOME_CAMERA.alpha),
  )

  const orbitRef = useRef<BABYLON.ArcRotateCamera | null>(null)
  const walkRef = useRef<BABYLON.UniversalCamera | null>(null)
  const walkPadRef = useRef<WalkPadInput>({ forward: 0, strafe: 0 })
  const camGoalRef = useRef<CameraGoal | null>(null)
  const viewRef = useRef<StudioView>('orbit')
  const classicFramingRef = useRef<ClassicFraming | null | undefined>(classicFraming)
  classicFramingRef.current = classicFraming
  const isClassicModeRef = useRef(isClassicMode)
  isClassicModeRef.current = isClassicMode
  // Read live inside the mount effect's per-frame loop (deps `[scene,
  // canvasRef]` only — see its own comment) without forcing a camera
  // remount if a caller ever passes a non-constant `classicZoom`.
  const classicZoomRef = useRef(classicZoom)
  classicZoomRef.current = classicZoom
  // Mount-time snapshot only — read via ref inside the mount effect so
  // changing/re-fetching `initialCamera` later (e.g. a slow scene-note
  // fetch resolving after the effect already ran) never re-seeds a camera
  // the user has since moved.
  const initialCameraRef = useRef(initialCamera)
  initialCameraRef.current = initialCamera

  useEffect(() => {
    if (!scene || !canvasRef.current) return
    const canvas = canvasRef.current
    const home = HOME_CAMERA
    // Mount-time snapshot — if the room already wants Classic (e.g. reopening
    // a gallery saved in Classic mode), start at the holding pose rather than
    // eye-level; the framing effect below corrects it the instant the room's
    // real bounds resolve. A saved `initialCamera` (see StudioCameraState)
    // wins over both defaults — reopening a gallery lands wherever the
    // owner last left the camera instead of always resetting home. Any
    // saved pose that's out of range for whichever mode we're mounting into
    // (e.g. a Studio-only alpha reopened straight into Classic) self-heals
    // on the very next frame via ArcRotateCamera's own limit clamping,
    // once the limits below are applied — no manual clamping needed here.
    const saved = initialCameraRef.current
    const startClassic = isClassicModeRef.current
    const initAlpha = saved?.alpha ?? (startClassic ? CLASSIC_HOLD.alpha : home.alpha)
    const initBeta = saved?.beta ?? (startClassic ? CLASSIC_HOLD.beta : home.beta)
    const initRadius = saved?.radius ?? (startClassic ? CLASSIC_HOLD.radius : home.radius)
    const initTarget = saved
      ? new BABYLON.Vector3(saved.target.x, saved.target.y, saved.target.z)
      : startClassic
        ? CLASSIC_HOLD.target.clone()
        : new BABYLON.Vector3(home.target.x, home.target.y, home.target.z)
    // Classic has no collision geometry (see `canWalk` below) — never trust
    // a saved walk pose from before the room switched to Classic.
    const startWalk = saved?.view === 'walk' && !!saved.walkPosition && !startClassic

    const orbit = new BABYLON.ArcRotateCamera('orbit', initAlpha, initBeta, initRadius, initTarget, scene)
    orbit.minZ = 0.05
    orbit.maxZ = 200
    if (startClassic) {
      orbit.lowerRadiusLimit = 0.5
      orbit.upperRadiusLimit = 16
      orbit.lowerAlphaLimit = orbit.upperAlphaLimit = Math.PI / 2
      orbit.lowerBetaLimit = CLASSIC_BETA_MIN
      orbit.upperBetaLimit = CLASSIC_BETA_MAX
    } else {
      orbit.lowerRadiusLimit = 2.2
      orbit.upperRadiusLimit = 34
      orbit.lowerBetaLimit = 0.03
      orbit.upperBetaLimit = 1.55
    }
    // Legacy pattern editor's Classic camera used `wheelPrecision = 100`
    // (src/pages/pattern/renderer/main.js `createCamera`) — Babylon divides
    // the raw wheel delta by this, so bigger means slower/more controlled
    // zoom per notch. This studio's own procedural (non-Classic) rooms keep
    // the faster, previously-tuned 22; Classic now matches the legacy feel
    // the side-by-side `/studio-classic` route (still running that exact
    // renderer) sets the bar for.
    orbit.wheelPrecision = startClassic ? 100 : 22
    orbit.panningSensibility = 700
    if (!startWalk) orbit.attachControl(canvas, true)

    const walk = new BABYLON.UniversalCamera(
      'walk',
      startWalk ? new BABYLON.Vector3(saved!.walkPosition!.x, saved!.walkPosition!.y, saved!.walkPosition!.z) : new BABYLON.Vector3(0, 1.62, -5.5),
      scene,
    )
    walk.minZ = 0.05
    walk.fov = 0.95
    walk.speed = 0.14
    walk.angularSensibility = 3200
    walk.keysUp = [87, 38]
    walk.keysDown = [83, 40]
    walk.keysLeft = [65, 37]
    walk.keysRight = [68, 39]
    if (startWalk && saved!.walkRotationY != null) walk.rotation.y = saved!.walkRotationY!
    else walk.setTarget(new BABYLON.Vector3(0, 1.8, 4))

    if (startWalk) {
      scene.activeCamera = walk
      walk.attachControl(canvas, true)
      viewRef.current = 'walk'
      setViewState('walk')
    } else {
      scene.activeCamera = orbit
    }
    orbitRef.current = orbit
    walkRef.current = walk

    const pipeline = new BABYLON.DefaultRenderingPipeline('studio-pipe', true, scene, [orbit, walk])
    pipeline.fxaaEnabled = true
    pipeline.bloomEnabled = true
    // A print's downward spotlight lights a small floor patch brightly by
    // design (museum-style pool of light) — at a low/oblique orbit angle that
    // patch's on-screen footprint is tiny, but a kernel this wide smeared it
    // into a room-dominating soft dome (worst in the darker Loft preset,
    // where nothing else in frame competes with it). Tighter
    // kernel + higher threshold keeps bloom on genuinely small/bright points
    // (lamps, bulbs) instead of ballooning modest floor pools.
    pipeline.bloomThreshold = 0.86
    pipeline.bloomWeight = 0.2
    pipeline.bloomKernel = 24
    pipeline.bloomScale = 0.5
    pipeline.imageProcessingEnabled = true
    pipeline.imageProcessing.toneMappingEnabled = true
    pipeline.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES
    pipeline.imageProcessing.exposure = 1.5
    pipeline.imageProcessing.contrast = 1.07
    pipeline.imageProcessing.vignetteEnabled = true
    pipeline.imageProcessing.vignetteWeight = 1.5
    pipeline.imageProcessing.vignetteColor = new BABYLON.Color4(0.02, 0.01, 0.05, 0)

    const clearGoal = () => {
      camGoalRef.current = null
    }
    canvas.addEventListener('pointerdown', clearGoal, true)
    canvas.addEventListener('wheel', clearGoal, { passive: true, capture: true })

    // Camera-position autosave — see StudioCameraState. A plain closure
    // variable (not a ref) since it's only ever touched from inside this
    // one render-loop callback and its own cleanup below.
    let lastPose: { view: StudioView; alpha: number; beta: number; radius: number; tx: number; ty: number; tz: number; wx?: number; wy?: number; wz?: number; wry?: number } | null = null
    let settleTimer: ReturnType<typeof setTimeout> | null = null

    const damping = scene.onBeforeRenderObservable.add(() => {
      const goal = camGoalRef.current
      if (goal && scene.activeCamera === orbit) {
        const k = 1 - Math.exp((-7 * scene.getEngine().getDeltaTime()) / 1000)
        let done = true;
        (['alpha', 'beta', 'radius'] as const).forEach((p) => {
          const goalValue = goal[p]
          if (goalValue === undefined) return
          orbit[p] += (goalValue - orbit[p]) * k
          if (Math.abs(goalValue - orbit[p]) > 0.002) done = false
        })
        if (goal.target) {
          BABYLON.Vector3.LerpToRef(orbit.target, goal.target, k, orbit.target)
          if (BABYLON.Vector3.Distance(orbit.target, goal.target) > 0.01) done = false
        }
        if (done) camGoalRef.current = null
      }
      // Kept unconditional (not nested in the goal branch above) so the
      // zoom/pan readouts track *every* radius/alpha/target change, not only
      // goal-driven ones. Babylon's own wheel/drag input controllers mutate
      // `orbit.radius`/`orbit.alpha`/`orbit.target` directly every tick —
      // completely bypassing `camGoalRef` — so gating this on `goal` left
      // both readouts frozen at whatever they were after the *last* animated
      // move (typically their mount-time default) through any amount of raw
      // scroll-to-zoom or drag-to-pan.
      if (scene.activeCamera === orbit) {
        // Classic's "100%" baseline is its own authored resting radius, not
        // the procedural room's `HOME_CAMERA` — same baseline `resetZoom`
        // below flies back to, kept in sync with it here.
        const classic = classicFramingRef.current
        const homeRadius = classic ? classic.radius * classicZoomRef.current : isClassicModeRef.current ? CLASSIC_HOLD.radius : home.radius
        setZoomPercent(Math.round((homeRadius / orbit.radius) * 100))
        // Re-derives the beta ceiling for *this frame's* radius, not the
        // one-time value the framing effect set at load/reset — see
        // `classicBetaCapAt`. Without this, zooming in after that effect ran
        // left the tilt range stuck at whatever (often near-zero) cap the
        // room's starting radius happened to produce, even though a closer
        // radius is safe to tilt much further from. Babylon clamps
        // `orbit.beta` into these bounds itself every frame, so tightening
        // them back up on zoom-out reins in an existing tilt automatically.
        if (classic) {
          const liveBetaCap = classicBetaCapAt(classic, orbit.radius)
          orbit.lowerBetaLimit = Math.min(CLASSIC_BETA_MIN, liveBetaCap)
          orbit.upperBetaLimit = Math.min(CLASSIC_BETA_MAX, liveBetaCap)
        }
        // Classic's alpha is locked (see CLASSIC_BETA above) — `target.x` is
        // the axis actually moving there, not `alpha`, so read the slider's
        // live position off whichever one this room's mode actually drives.
        setOrbitAlphaPercentState(classic ? panXToSweepPercent(orbit.target.x, classic) : alphaToSweepPercent(orbit.alpha))
      }

      // On-screen walk pad (guest mobile) — keyboard still handled by UniversalCamera keys.
      const pad = walkPadRef.current
      if (scene.activeCamera === walk && (pad.forward || pad.strafe)) {
        const dt = Math.min(scene.getEngine().getDeltaTime() / 16.67, 2.5)
        const step = walk.speed * dt
        const forward = walk.getDirection(BABYLON.Axis.Z)
        const right = walk.getDirection(BABYLON.Axis.X)
        forward.y = 0
        right.y = 0
        if (forward.lengthSquared() > 1e-6) forward.normalize()
        if (right.lengthSquared() > 1e-6) right.normalize()
        walk.position.addInPlace(forward.scale(pad.forward * step))
        walk.position.addInPlace(right.scale(pad.strafe * step))
        walk.position.y = 1.62
      }

      // Cheap every-frame comparison; only resets the settle timer when the
      // pose actually moved (fly-to/damping included), so a static camera
      // costs nothing beyond a handful of number checks.
      const activeIsWalk = viewRef.current === 'walk'
      const pose = {
        view: viewRef.current,
        alpha: orbit.alpha,
        beta: orbit.beta,
        radius: orbit.radius,
        tx: orbit.target.x,
        ty: orbit.target.y,
        tz: orbit.target.z,
        ...(activeIsWalk
          ? { wx: walk.position.x, wy: walk.position.y, wz: walk.position.z, wry: walk.rotation.y }
          : {}),
      }
      const EPS = 1e-4
      const moved =
        !lastPose ||
        lastPose.view !== pose.view ||
        Math.abs(lastPose.alpha - pose.alpha) > EPS ||
        Math.abs(lastPose.beta - pose.beta) > EPS ||
        Math.abs(lastPose.radius - pose.radius) > EPS ||
        Math.abs(lastPose.tx - pose.tx) > EPS ||
        Math.abs(lastPose.ty - pose.ty) > EPS ||
        Math.abs(lastPose.tz - pose.tz) > EPS ||
        (activeIsWalk &&
          (Math.abs((lastPose.wx ?? 0) - (pose.wx ?? 0)) > EPS ||
            Math.abs((lastPose.wy ?? 0) - (pose.wy ?? 0)) > EPS ||
            Math.abs((lastPose.wz ?? 0) - (pose.wz ?? 0)) > EPS ||
            Math.abs((lastPose.wry ?? 0) - (pose.wry ?? 0)) > EPS))
      if (moved) {
        lastPose = pose
        if (settleTimer) clearTimeout(settleTimer)
        settleTimer = setTimeout(() => {
          settleTimer = null
          setCameraState({
            view: pose.view,
            alpha: pose.alpha,
            beta: pose.beta,
            radius: pose.radius,
            target: { x: pose.tx, y: pose.ty, z: pose.tz },
            ...(activeIsWalk ? { walkPosition: { x: pose.wx!, y: pose.wy!, z: pose.wz! }, walkRotationY: pose.wry } : {}),
          })
        }, CAMERA_SETTLE_MS)
      }
    })

    return () => {
      scene.onBeforeRenderObservable.remove(damping)
      if (settleTimer) clearTimeout(settleTimer)
      canvas.removeEventListener('pointerdown', clearGoal, true)
      canvas.removeEventListener('wheel', clearGoal)
      pipeline.dispose()
      orbit.dispose()
      walk.dispose()
      orbitRef.current = null
      walkRef.current = null
    }
  }, [scene, canvasRef])

  const animateTo = useCallback((goal: CameraGoal, replace = false) => {
    // `replace` — flyTo/tour must not merge with a leftover Orbit-home goal
    // (stale alpha/beta can never settle under Classic locks, and used to
    // fight print targets after Plan/Walk → tour).
    camGoalRef.current = replace ? { ...goal } : { ...(camGoalRef.current || {}), ...goal }
    // Orbit control stays attached during the fly (so a mid-flight drag can
    // still interrupt it via the pointerdown/wheel `clearGoal` listeners
    // below) — but that means any inertia left over from the drag/orbit the
    // user was just doing (tap a print right after spinning the room, or
    // tapping Play mid-orbit) keeps decaying via ArcRotateCamera's own
    // `_checkInputs()` every frame, on top of this goal's damped writes to
    // the same alpha/beta/radius/target. Two independent nudges compounding
    // on the same properties every frame is exactly what reads as the whole
    // gallery "shaking" instead of a clean fly-to. Zeroing it here is the
    // one point every animated camera move (flyTo, tour, view switches) goes
    // through, so this covers all of them.
    const orbit = orbitRef.current
    if (orbit) {
      orbit.inertialAlphaOffset = 0
      orbit.inertialBetaOffset = 0
      orbit.inertialRadiusOffset = 0
      orbit.inertialPanningX = 0
      orbit.inertialPanningY = 0
    }
  }, [])

  const setOrbitAlphaPercent = useCallback((percent: number) => {
    const clamped = Math.max(0, Math.min(100, percent))
    setOrbitAlphaPercentState(clamped)
    const orbit = orbitRef.current
    if (!orbit) return
    // A direct, un-damped write — same as dragging the canvas itself, not
    // an animated fly-to. Routing this through `animateTo`'s damping (as
    // this used to) fights the very value the drag is trying to set: the
    // per-frame loop below only re-syncs `orbitAlphaPercent` from wherever
    // the camera *actually* is, so a goal still easing toward the drag's
    // target kept snapping the slider back a frame after every tick
    // ("skips around" / rubber-banding while dragging). Clearing any
    // in-flight goal too — same as the canvas's own pointerdown/wheel
    // listeners already do — so a leftover flyTo/view-switch animation
    // can't fight this override on the next frame.
    camGoalRef.current = null
    const classic = classicFramingRef.current
    if (classic) {
      // Alpha's locked here (see CLASSIC_BETA above) — pan `target.x`
      // instead, same axis the legacy pattern editor's own
      // `SetCameraHorizontalPosition` panned (see `ClassicFraming`).
      orbit.target.x = sweepPercentToPanX(clamped, classic)
    } else {
      orbit.alpha = sweepPercentToAlpha(clamped)
    }
  }, [])

  /**
   * Direct, un-damped radius write — same "instant, like dragging" choice
   * `setOrbitAlphaPercent` makes above, so a stepper click feels like one
   * tactile notch rather than a slow eased glide. The mount effect's
   * per-frame loop would also pick this up on the very next frame, but
   * setting it here too makes the readout update in the same tick as the
   * click rather than waiting one.
   */
  const setZoomRadius = useCallback((radius: number) => {
    const orbit = orbitRef.current
    if (!orbit) return
    const lower = orbit.lowerRadiusLimit ?? 0.5
    const upper = orbit.upperRadiusLimit ?? 40
    const clamped = Math.max(lower, Math.min(upper, radius))
    camGoalRef.current = null
    orbit.radius = clamped
    // Same classic-aware "100%" baseline as the per-frame sync above and
    // `resetZoom` below — this used to always divide against the
    // procedural `HOME_CAMERA.radius`, which is a nonsense baseline for a
    // Classic room's own (usually very different) authored resting radius.
    const classic = classicFramingRef.current
    const homeRadius = classic ? classic.radius * classicZoom : isClassicModeRef.current ? CLASSIC_HOLD.radius : HOME_CAMERA.radius
    setZoomPercent(Math.round((homeRadius / clamped) * 100))
  }, [classicZoom])

  const zoomIn = useCallback(() => {
    const orbit = orbitRef.current
    if (!orbit) return
    setZoomRadius(orbit.radius / ZOOM_STEP_FACTOR)
  }, [setZoomRadius])

  const zoomOut = useCallback(() => {
    const orbit = orbitRef.current
    if (!orbit) return
    setZoomRadius(orbit.radius * ZOOM_STEP_FACTOR)
  }, [setZoomRadius])

  const setView = useCallback(
    (nextView: StudioView) => {
      const orbit = orbitRef.current
      const walk = walkRef.current
      const scn = scene
      if (!orbit || !walk || !scn) return
      // Walk has no collision detection — fine over the procedural shell
      // (always a simple convex box), but a loaded Classic room can have
      // columns/alcoves a camera would clip straight through. Stay in Orbit.
      const classic = classicFramingRef.current
      if (nextView === 'walk' && classic) return
      const home = HOME_CAMERA
      const homeTarget = new BABYLON.Vector3(home.target.x, home.target.y, home.target.z)
      // A Classic room's own locked-alpha top-down orbit (set by the framing
      // effect above) stands in for both "Orbit" and "Plan" here — its
      // geometry doesn't support the procedural shell's home/plan angles.
      // Plan resets closer to true top-down (CLASSIC_BETA_PLAN) than Orbit's
      // default (CLASSIC_BETA), same distinction the legacy pattern editor
      // drew, but both stay within the same beta range so users can still
      // tilt freely from either reset point.
      //
      // `isClassicModeRef.current && !classic` — the room *wants* Classic
      // but its `.babylon` file hasn't finished loading yet (async, see
      // useStudioLegacyRoom). Falling through to the procedural `home`
      // pose below is wrong here: `home`'s target/radius describe a
      // completely different (and much larger-scale) room, so animating
      // toward it lands the camera in empty space near this room's
      // ceiling with no floor in view — worse, that animation keeps
      // running (`camGoalRef` persists across frames) and fights the
      // correct pose the framing effect above sets the instant loading
      // *does* finish. Hold at the same neutral pose the mount-time init
      // uses instead — a no-op until real framing arrives, not a wrong one.
      const resetGoal = classic
        ? {
            alpha: Math.PI / 2,
            beta: Math.min(nextView === 'plan' ? CLASSIC_BETA_PLAN : CLASSIC_BETA, classicBetaCapAt(classic, classic.radius * classicZoom)),
            radius: classic.radius * classicZoom,
            target: classic.target.clone(),
          }
        : isClassicModeRef.current
          ? { alpha: CLASSIC_HOLD.alpha, beta: nextView === 'plan' ? CLASSIC_BETA_PLAN : CLASSIC_HOLD.beta, radius: CLASSIC_HOLD.radius, target: CLASSIC_HOLD.target.clone() }
          : nextView === 'plan'
            ? { alpha: -Math.PI / 2, beta: orbit.lowerBetaLimit ?? 0.03, radius: 17, target: new BABYLON.Vector3(0, 0, 0) }
            : { alpha: home.alpha, beta: home.beta, radius: home.radius, target: homeTarget }

      if (nextView === viewRef.current) {
        if (nextView === 'orbit') animateTo(resetGoal)
        return
      }
      viewRef.current = nextView
      setViewState(nextView)
      orbit.detachControl()
      walk.detachControl()

      if (nextView === 'walk') {
        scn.activeCamera = walk
        walk.position.set(0, 1.62, -5.5)
        walk.setTarget(new BABYLON.Vector3(0, 1.8, 4))
        walk.attachControl(canvasRef.current || undefined, true)
        setZoomPercent(0)
      } else {
        scn.activeCamera = orbit
        orbit.attachControl(canvasRef.current || undefined, true)
        animateTo(resetGoal)
      }
    },
    [scene, canvasRef, animateTo, classicZoom],
  )

  // Re-frame Orbit whenever a Classic room finishes loading (or the studio
  // switches back to a procedural preset). These rooms were authored for
  // the legacy pattern editor's fixed bird's-eye camera — alpha locked at
  // π/2, beta clamped to a near-top-down sliver (see `createCamera` in
  // src/pages/pattern/renderer/main.js) — not a free eye-level orbit like
  // `HOME_CAMERA`. Using the studio's usual alpha/beta here renders these
  // rooms sideways/"flipped", since their geometry was authored assuming
  // this exact locked-down viewing angle.
  useEffect(() => {
    const orbit = orbitRef.current
    if (!orbit) return
    // Keeps pace with the mount-time `wheelPrecision` pick below even when
    // Classic is entered/left without a remount (e.g. swapping a room's
    // template between Classic and a procedural preset) — see that
    // assignment's comment for why Classic gets the legacy studio's slower
    // value here instead of the procedural default.
    orbit.wheelPrecision = isClassicMode ? 100 : 22
    if (classicFraming) {
      const scaledRadius = classicFraming.radius * classicZoom
      const scaledRadiusMax = classicFraming.radiusMax * classicZoom
      orbit.lowerRadiusLimit = Math.max(0.5, CLASSIC_LOWER_RADIUS * classicZoom)
      orbit.upperRadiusLimit = scaledRadiusMax
      orbit.radius = scaledRadius
      orbit.target.copyFrom(classicFraming.target)
      orbit.alpha = Math.PI / 2
      const startBetaCap = classicBetaCapAt(classicFraming, scaledRadius)
      orbit.beta = Math.min(CLASSIC_BETA, startBetaCap)
      orbit.lowerAlphaLimit = orbit.upperAlphaLimit = Math.PI / 2
      orbit.lowerBetaLimit = Math.min(CLASSIC_BETA_MIN, startBetaCap)
      orbit.upperBetaLimit = Math.min(CLASSIC_BETA_MAX, startBetaCap)
      // Real pan bounds just arrived (or this room's template just swapped
      // to a differently-sized one) — resync the slider off the freshly
      // (re)centered `target.x` instead of leaving it at the previous
      // template's position, or the pre-load 50% guess.
      setOrbitAlphaPercentState(panXToSweepPercent(orbit.target.x, classicFraming))
      if (viewRef.current === 'walk') setView('orbit')
    } else if (isClassicMode) {
      // Classic mode is requested but not framed yet — still loading its
      // `.babylon` file, or stuck with no `classicTemplateId` at all. Either
      // way `useStudioRoom` has skipped building any procedural floor, so
      // `HOME_CAMERA`'s walkable eye-level pose has nothing to stand on.
      // Hold the same neutral top-down pose the mount-time init above uses.
      orbit.lowerRadiusLimit = CLASSIC_HOLD.radius * 0.4
      orbit.upperRadiusLimit = 16
      orbit.radius = CLASSIC_HOLD.radius
      orbit.target.copyFrom(CLASSIC_HOLD.target)
      orbit.alpha = CLASSIC_HOLD.alpha
      orbit.beta = CLASSIC_HOLD.beta
      orbit.lowerAlphaLimit = orbit.upperAlphaLimit = Math.PI / 2
      orbit.lowerBetaLimit = CLASSIC_BETA_MIN
      orbit.upperBetaLimit = CLASSIC_BETA_MAX
      // No real bounds yet (still loading, or no template at all) — hold
      // centered, same as the mount-time init above.
      setOrbitAlphaPercentState(50)
      if (viewRef.current === 'walk') setView('orbit')
    } else {
      orbit.lowerRadiusLimit = 2.2
      orbit.upperRadiusLimit = 34
      orbit.lowerAlphaLimit = null
      orbit.upperAlphaLimit = null
      orbit.lowerBetaLimit = 0.03
      orbit.upperBetaLimit = 1.55
      // Coming back from Classic — its locked top-down alpha/beta/target
      // are meaningless for the procedural shell, so snap straight back to
      // its home framing rather than leaving the camera stuck looking
      // straight down at whichever room just rebuilt.
      const home = HOME_CAMERA
      orbit.alpha = home.alpha
      orbit.beta = home.beta
      orbit.radius = home.radius
      orbit.target.set(home.target.x, home.target.y, home.target.z)
      // Back to a procedural room — the slider goes back to sweeping alpha.
      setOrbitAlphaPercentState(alphaToSweepPercent(home.alpha))
    }
  }, [classicFraming, isClassicMode, setView, classicZoom])

  /**
   * Switch to the orbit camera without the Orbit-button home reset.
   * Tour / Layers flyTo used to call `setView('orbit')`, which animates to
   * HOME and races (or wins via merge) against the print goal — play looked
   * broken from Plan/Walk.
   */
  const ensureOrbitCamera = useCallback(() => {
    const orbit = orbitRef.current
    const walk = walkRef.current
    const scn = scene
    if (!orbit || !walk || !scn) return null
    if (viewRef.current === 'orbit' && scn.activeCamera === orbit) return orbit
    viewRef.current = 'orbit'
    setViewState('orbit')
    orbit.detachControl()
    walk.detachControl()
    scn.activeCamera = orbit
    orbit.attachControl(canvasRef.current || undefined, true)
    return orbit
  }, [scene, canvasRef])

  /**
   * Zoom-only reset — same home radius per mode `setView`'s own "re-click
   * Orbit while active" branch flies back to, but written directly (like
   * `setZoomRadius` above) rather than through `animateTo`, and leaving
   * alpha/beta/target untouched. Doesn't require the orbit camera to
   * already be attached/active the way `ensureOrbitCamera` does — this is
   * a quiet value reset, not a view switch.
   */
  const resetZoom = useCallback(() => {
    const home = HOME_CAMERA
    const classic = classicFramingRef.current
    const homeRadius = classic ? classic.radius * classicZoom : isClassicModeRef.current ? CLASSIC_HOLD.radius : home.radius
    setZoomRadius(homeRadius)
  }, [classicZoom, setZoomRadius])

  /**
   * Full "back to where this loaded" reset — everything `resetZoom` above
   * deliberately leaves alone (pan/alpha, tilt/beta, target), plus Walk's
   * own position/target for a procedural room. Present mode and the public
   * Guest route have no equivalent of the Studio editor's separate Pan/Zoom
   * pills (hidden entirely in Present, never built for Guest at all) — a
   * viewer who's dragged an orbit around or walked off has no other way
   * back to the room's starting view, so this is the one reset they get
   * instead of two axis-specific ones.
   */
  const resetCamera = useCallback(() => {
    const walk = walkRef.current
    if (viewRef.current === 'walk' && walk) {
      // Same starting pose `setView`'s own walk branch sets — deliberately
      // not `ensureOrbitCamera` + the orbit resetGoal below, which would
      // also switch the view mode back to Orbit; a "reset camera" click
      // mid-walk should snap position, not kick the viewer out of walking.
      walk.position.set(0, 1.62, -5.5)
      walk.setTarget(new BABYLON.Vector3(0, 1.8, 4))
      return
    }
    const orbit = ensureOrbitCamera()
    if (!orbit) return
    const home = HOME_CAMERA
    const classic = classicFramingRef.current
    const resetGoal: CameraGoal = classic
      ? {
          alpha: Math.PI / 2,
          beta: Math.min(CLASSIC_BETA, classicBetaCapAt(classic, classic.radius * classicZoom)),
          radius: classic.radius * classicZoom,
          target: classic.target.clone(),
        }
      : isClassicModeRef.current
        ? { alpha: CLASSIC_HOLD.alpha, beta: CLASSIC_HOLD.beta, radius: CLASSIC_HOLD.radius, target: CLASSIC_HOLD.target.clone() }
        : { alpha: home.alpha, beta: home.beta, radius: home.radius, target: new BABYLON.Vector3(home.target.x, home.target.y, home.target.z) }
    animateTo(resetGoal, true)
  }, [animateTo, classicZoom, ensureOrbitCamera])

  const flyTo = useCallback(
    (target: FlyToTarget) => {
      const orbit = ensureOrbitCamera()
      if (!orbit) return
      // Classic: locked top-down alpha/beta — pan target + zoom to the print
      // (radius-only fly used to leave the camera stuck on room center).
      if (classicFramingRef.current) {
        animateTo(
          {
            radius: Math.max(1.4, 1.6 + target.width * 0.9),
            target: target.center.clone(),
          },
          true,
        )
        return
      }
      let alpha = target.side === -1 ? 0 : target.side === 1 ? Math.PI : -Math.PI / 2
      while (alpha - orbit.alpha > Math.PI) alpha -= 2 * Math.PI
      while (alpha - orbit.alpha < -Math.PI) alpha += 2 * Math.PI
      animateTo(
        { alpha, beta: 1.42, radius: 2.4 + target.width * 1.15, target: target.center.clone() },
        true,
      )
    },
    [animateTo, ensureOrbitCamera],
  )

  const enterPresent = useCallback(() => {
    setIsPresent(true)
    // Classic rooms have no collision over their authored geometry, so
    // first-person Walk doesn't work there — but Present itself isn't
    // walk-specific, it's "hide the editor chrome": falling back to Orbit
    // (already alpha/beta-bounded to this room's own limits, set above)
    // instead of bailing out lets Classic galleries present read-only too.
    setView(isClassicModeRef.current ? 'orbit' : 'walk')
  }, [setView])

  const exitPresent = useCallback(() => {
    setIsPresent(false)
    setView('orbit')
  }, [setView])

  const activeCameraInfo = useMemo(
    () => ({ camera: view === 'walk' ? walkRef.current : orbitRef.current, isInside: view === 'walk' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view],
  )

  // Stable getter for dollhouse cutaway — must not depend on a memoized
  // snapshot that was null before cameras mounted (Plan click used to "fix" that).
  const getActiveCameraInfo = useCallback(
    () => ({
      camera: (viewRef.current === 'walk' ? walkRef.current : orbitRef.current) as BABYLON.Camera | null,
      isInside: viewRef.current === 'walk',
    }),
    [],
  )

  const canWalk = !classicFraming

  const setWalkPad = useCallback((input: Partial<WalkPadInput>) => {
    walkPadRef.current = { ...walkPadRef.current, ...input }
  }, [])

  return useMemo(
    () => ({
      view,
      isPresent,
      zoomPercent,
      setView,
      flyTo,
      enterPresent,
      exitPresent,
      activeCameraInfo,
      getActiveCameraInfo,
      canWalk,
      setWalkPad,
      cameraState,
      orbitAlphaPercent,
      setOrbitAlphaPercent,
      zoomIn,
      zoomOut,
      resetZoom,
      resetCamera,
    }),
    [
      view,
      isPresent,
      zoomPercent,
      setView,
      flyTo,
      enterPresent,
      exitPresent,
      activeCameraInfo,
      getActiveCameraInfo,
      canWalk,
      setWalkPad,
      cameraState,
      orbitAlphaPercent,
      setOrbitAlphaPercent,
      zoomIn,
      zoomOut,
      resetZoom,
      resetCamera,
    ],
  )
}
